<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';
import HrSkeleton from './HrSkeleton.vue';

/**
 * M3 chip primitive with assist, filter, input, and suggestion variants.
 *
 * @status internal
 * @group chips
 * @a11y A clickable chip announces as a button, and both its targets activate on Enter and Space.
 * Quasar gives the body neither, and its remove button Enter alone, so the two halves of one chip
 * used to describe themselves differently and answer different keys.
 * @see https://quasar.dev/vue-components/chip Quasar q-chip API
 */
defineOptions({ name: 'HrChip', inheritAttrs: false });

/*
 * Inheritance is off because the attributes are forwarded by hand below, and leaving it on applied
 * every one of them a second time. Vue dedupes identical listeners, which is why that went
 * unnoticed, but the role and key handler are routed the same way now and a duplicated binding is
 * invisible from the call site. The cost is that each branch of the template has to forward them
 * itself, the pending one included.
 */

const props = defineProps({
  /** Text label rendered inside the chip. */
  label: {
    type: String,
    default: null,
  },
  /**
   * M3 chip size. sm = 24dp (density -2, for compact containers), md = 32dp (standard), lg = 56dp (expressive).
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /**
   * M3 chip variant — set by semantic chip components.
   * When omitted the base chip style (transparent + outline border) is used.
   * @values assist, filter, input, suggestion, status-action
   */
  variant: {
    type: String,
    default: null,
    validator: (v) => v === null || ['assist', 'filter', 'input', 'suggestion', 'status-action'].includes(v),
  },
  /** Material icon name shown as a leading icon inside the chip. */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Material icon name shown as a trailing icon inside the chip, after the label.
   *
   * Declared rather than left to attribute fallthrough: `HrStatusActionChip` has been passing it
   * since it was written, so the capability existed while the API denied it.
   */
  iconRight: {
    type: String,
    default: null,
  },
  /** Material icon name for the remove button. Defaults to the chip's standard remove icon when null. */
  iconRemove: {
    type: String,
    default: null,
  },
  /**
   * Accessible name for the remove button. Defaults to Quasar's generic "Remove" when null,
   * which is ambiguous once the chip body carries its own action — name the value being removed.
   */
  removeAriaLabel: {
    type: String,
    default: null,
  },
  /** When true, shows a trailing remove button that emits a remove event. */
  removable: {
    type: Boolean,
    default: false,
  },
  /** When true, the chip is interactive and responds to hover, focus, and click. */
  clickable: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the chip. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Selected state — used by filter chips to reflect toggled-on state.
   */
  selected: {
    type: Boolean,
    default: false,
  },
  /**
   * Elevated surface style — used by assist chips.
   */
  elevated: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders a pill-shaped skeleton placeholder instead of the chip.
   * Use when the chip's content is not yet available.
   */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * Quasar props (color, textColor, outline, …) applied in legacy (non-theme) mode.
   * Ignored when a design system theme is active.
   * @deprecated Use semantic chip components; pass legacy props only for backwards compatibility.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const SPACE_KEY_CODE = 32;

const REMOVE_BUTTON = '.q-chip__icon--remove';

/*
 * Quasar activates a chip on Enter and ignores Space, which the button role below promises will
 * work. Both of the chip's targets are covered: the body, and the remove button, which is a focus
 * stop of its own and whose keyup passes through here on the way up.
 *
 * Dispatching a click rather than emitting one keeps every listener in agreement, and is how
 * Quasar's own list item closes the same gap. Aimed at whichever target holds focus, so a remove
 * that stops the click reaching the body from a mouse stops it the same way from the keyboard.
 *
 * Anything else focusable inside a chip is left alone. A control placed in the slot activates
 * itself on Space, and a click dispatched at it here would be the second of two.
 */
function onKeyup(event) {
  if (event.keyCode !== SPACE_KEY_CODE) {
    return;
  }

  const target = event.target === event.currentTarget
    ? event.currentTarget
    : event.target.closest(REMOVE_BUTTON);

  if (target === null) {
    return;
  }

  event.preventDefault();
  target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
}

/*
 * Quasar gives the body a tabindex and no role, so a chip that acts announces as static text. The
 * keys are bound for a removable chip too, which has a target to answer for whether or not its
 * body does anything.
 */
const actionAttrs = computed(() => {
  if (props.disable === true) {
    return {};
  }

  const role = props.clickable === true ? { role: 'button' } : {};
  const keyboard = props.clickable === true || props.removable === true ? { onKeyup } : {};

  return { ...role, ...keyboard };
});

const classes = computed(() => [
  'hr-chip',
  `hr-chip--size-${props.size}`,
  props.variant !== null && `hr-chip--${props.variant}`,
  props.selected && 'hr-chip--selected',
  props.elevated && 'hr-chip--elevated',
]);

/*
 * Quasar reserves the leading slot for any icon name that is not undefined, so an empty one gets a
 * blank gap the width of an icon instead of no icon at all. Callers reach an optional icon through
 * an expression that yields '' as readily as null, so both have to mean the same thing here.
 */
const hasIcon = computed(() => props.icon !== null && props.icon !== '');

const effectiveProps = computed(() => {
  const base = {
    ...(props.label !== null && { label: props.label }),
    ...(hasIcon.value && { icon: props.icon }),
    ...(props.iconRight !== null && { iconRight: props.iconRight }),
    ...(props.iconRemove !== null && { iconRemove: props.iconRemove }),
    ...(props.removeAriaLabel !== null && { removeAriaLabel: props.removeAriaLabel }),
    removable: props.removable,
    clickable: props.clickable,
    disable: props.disable,
    ...actionAttrs.value,
  };

  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, size: props.size, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <hr-skeleton
    v-if="props.pending"
    v-bind="attrs"
    type="pill"
    :height="`var(--app-comp-chip-container-height-${props.size})`"
  />
  <q-chip
    v-else
    v-bind="effectiveProps"
    :ripple="clickable"
    :class="[hasTheme ? classes : 'hr-chip-icon-fix', 'hr-chip-flush']"
  >
    <!-- @slot default Custom chip content replacing the `label` prop. -->
    <slot />
  </q-chip>
</template>

<style lang="scss" scoped>

/*
 * Quasar gives every chip 4px of its own margin. A chip cannot know what it sits in, so the space
 * between chips belongs to the container: with the margin in place a container using gap gets 4px
 * it never asked for, and a chip stands taller than the room reserved for it. Cleared in both
 * themes, since the container owns the layout either way.
 */
.hr-chip-flush {
  margin: 0;
}

/*
 * Legacy icon-spacing fix (applied when no design-system theme is active). The remove icon is
 * exempt: its left margin is the gap between it and the label, so zeroing that runs the two
 * together on a removable chip.
 */
.hr-chip-icon-fix :deep(.q-chip__icon:not(.q-chip__icon--remove)) {
  margin-top: 0;
  margin-bottom: 0;
  margin-left: 0;
}

/* Only applies to light/dark themes */

/* ── Base ────────────────────────────────────────────────── */
.hr-chip {
  height: var(--app-comp-chip-container-height-md);
  padding: 0 8px;
  font-size: var(--app-comp-chip-label-text-size-md);
  border-radius: var(--app-shape-corner-small);
  background: transparent;
  color: var(--_chip-color, var(--app-on-surface));
  border: 1px solid var(--_chip-border, var(--app-outline));
}

.hr-chip--size-sm {
  height: var(--app-comp-chip-container-height-sm);
  font-size: var(--app-comp-chip-label-text-size-sm);
}

.hr-chip--size-sm :deep(.q-chip__icon) {
  font-size: var(--app-comp-chip-leading-icon-size-sm);
}

.hr-chip--size-lg {
  height: var(--app-comp-chip-container-height-lg);
  font-size: var(--app-comp-chip-label-text-size-lg);
}

/*
 * These 8dp and the container's above are the whole of M3's chip spacing, and Quasar's structure
 * applies the conditional half of the spec for free: a leading icon is a sibling ahead of
 * .q-chip__content, so a label-only chip gets 8 + 8 = 16dp at its leading edge while one carrying
 * an icon gets the container's 8dp, which is what the spec asks in each case. The same 8dp is the
 * gap between an icon and the label, and between the label and a remove button.
 */
.hr-chip :deep(.q-chip__content) {
  padding: 0 8px;
}

/* ── Icons ───────────────────────────────────────────────── */
/*
 * Quasar hardcodes .q-chip__icon { color: rgba(0,0,0,.54); font-size: 1.5em }
 * and uses em-based margins as spacing hacks. Reset both so our layout is
 * fully controlled by the content padding above.
 */
.hr-chip :deep(.q-chip__icon) {
  color: currentColor;
  font-size: var(--app-comp-chip-leading-icon-size-md);
  margin: 0;
}

.hr-chip--size-lg :deep(.q-chip__icon) {
  font-size: var(--app-comp-chip-leading-icon-size-lg);
}

/* ── Assist ──────────────────────────────────────────────── */
.hr-chip--assist :deep(.q-chip__icon--left) {
  color: var(--_chip-icon-color, var(--app-primary));
}

.hr-chip--assist.hr-chip--elevated {
  background: var(--app-surface-container-low);
  border-color: transparent;
  box-shadow: var(--app-elevation-1);
}

/* ── Filter ──────────────────────────────────────────────── */
.hr-chip--filter {
  color: var(--app-on-surface-variant);
}

.hr-chip--filter.hr-chip--selected {
  background: var(--app-secondary-container);
  border-color: transparent;
  color: var(--app-on-secondary-container);
}

/* ── Input ───────────────────────────────────────────────── */
/*
 * M3's selected input chip, because an input chip stands for a value the user actively chose, so
 * it takes the secondary-container fill and its matching label rather than the neutral
 * surface-variant pair, which lands close to how a disabled control reads.
 *
 * Transparent rather than absent border: M3 draws no outline here, and keeping the 1px keeps a
 * chip the same width as the variants that do.
 */
.hr-chip--input {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);
  border-color: transparent;

  /*
   * An input chip is the one variant M3 spaces at 12dp rather than 16dp, being denser and
   * usually carrying a remove button. Without this the shared container padding would widen it.
   */
  padding-inline-start: 4px;
}

/*
 * The trailing edge only needs the same trim when nothing follows the label. A trailing icon sits
 * against the container's 8dp, which is what M3 asks for there, but a bare label reaches the edge
 * through its own 8dp on top and would otherwise render 16dp against the leading edge's 12.
 */
.hr-chip--input:not(:has(.q-chip__icon--right, .q-chip__icon--remove)) {
  padding-inline-end: 4px;
}

/* ── Suggestion ──────────────────────────────────────────── */
.hr-chip--suggestion {
  color: var(--app-on-surface);
}

/* ── Status Action ───────────────────────────────────────── */
.hr-chip--status-action.hr-chip--selected {
  background: var(--app-secondary-container);
  border-color: transparent;
  color: var(--app-on-secondary-container);
}

/* ── State layer ─────────────────────────────────────────── */
/*
 * Quasar renders .q-focus-helper on clickable chips and colors it currentColor, but only
 * on hover, and composites a black ::before and white ::after over it. Zero those and drive
 * the opacity ourselves for M3's 8% hover / 12% focus and press.
 *
 * currentColor is deliberate: each variant already sets its own text role above, so the
 * state layer follows the chip's role without a rule per variant.
 *
 * Quasar scopes the whole .q-focus-helper block to body.desktop, so hover matches that;
 * focus-visible and active do not, to keep keyboard and touch feedback on every platform.
 */
.hr-chip > :deep(.q-focus-helper) {
  background: currentcolor;
}

.hr-chip > :deep(.q-focus-helper::before),
.hr-chip > :deep(.q-focus-helper::after) {
  opacity: 0 !important;
}

/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-chip.q-chip--clickable:not(.disabled):hover > :deep(.q-focus-helper) {
  opacity: 0.08;
}

.hr-chip.q-chip--clickable:not(.disabled):focus-visible > :deep(.q-focus-helper),
.hr-chip.q-chip--clickable:not(.disabled):active > :deep(.q-focus-helper) {
  opacity: 0.12;
}

/*
 * The remove button sits inside the chip, so pointing at it also matches the chip's own :hover and
 * :active and lights both layers at once. M3 shows only the trailing icon's here, keeping the two
 * targets legible as separate things. These repeat the selectors above rather than sitting on a
 * shorter one, because :has() carries only its argument's specificity and would otherwise lose.
 */
body.desktop .hr-chip.q-chip--clickable:not(.disabled):has(.q-chip__icon--remove:hover) > :deep(.q-focus-helper) {
  opacity: 0;
}

.hr-chip.q-chip--clickable:not(.disabled):has(.q-chip__icon--remove:active) > :deep(.q-focus-helper) {
  opacity: 0;
}

/* ── Remove button ───────────────────────────────────────── */
/*
 * Quasar dims the remove icon to .6 and lifts it on hover. M3 keeps the trailing icon at
 * full strength and gives it its own state layer, which also keeps it distinguishable from
 * the chip body when both are interactive.
 */
.hr-chip :deep(.q-chip__icon--remove) {
  opacity: 1;
  border-radius: 50%;
}

.hr-chip :deep(.q-chip__icon--remove:hover),
.hr-chip :deep(.q-chip__icon--remove:focus-visible) {
  background: color-mix(in srgb, currentcolor 12%, transparent);
}

/* ── Disabled ────────────────────────────────────────────── */
.hr-chip.disabled {
  opacity: 1 !important;
  background: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
  border-color: transparent;
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

</style>
